import { Button as AntButton } from 'antd';
import type { ButtonProps as AntButtonProps } from 'antd';
import styles from './Button.module.scss';

/**
 * Visual treatments layered on top of Ant Design's Button:
 * - `default` — a plain AntD Button (drop-in passthrough).
 * - `glass`   — the prototype's pill glass CTA (surface + dispersion ring + accent blob).
 * - `solid`   — a glowing brand-filled pill (uses AntD `type="primary"` colors; pass
 *               `danger` to swap the purple glow for a red one — AntD's own `danger`
 *               prop already recolors the fill, this only fixes the glow to match).
 *               Pass `accent="green"` (or `"yellow"`) for a green-2 (yellow) fill instead — AntD has no
 *               built-in "success" Button prop to lean on for this one, so the
 *               fill/border/text are set directly rather than riding a prop.
 * - `outline` — a bordered, transparent pill — the standard secondary/cancel button
 *               paired with `solid` in every modal footer across the app.
 *
 * Named `appearance` to avoid clashing with AntD's own `variant` (fill style).
 * See "Buttons" in apps/web/CLAUDE.md.
 */
export type ButtonAppearance = 'default' | 'glass' | 'solid' | 'outline';

/** Accent color — the glow/blob color for `glass`, the fill for `solid` (`danger` still wins if both are set). */
export type ButtonAccent = 'purple' | 'green' | 'yellow';

// AntD's ButtonProps carries a `data-${string}` template index signature that
// breaks JSX prop inference when reused on a wrapper component. Strip it.
type OmitDataIndex<T> = {
  [K in keyof T as K extends `data-${string}` ? never : K]: T[K];
};

export type ButtonProps = OmitDataIndex<AntButtonProps> & {
  appearance?: ButtonAppearance;
  accent?: ButtonAccent;
};

export const Button = ({
  appearance = 'default',
  accent = 'purple',
  className,
  type,
  danger,
  ...rest
}: ButtonProps) => {
  if (appearance === 'default') {
    return <AntButton type={type} danger={danger} className={className} {...rest} />;
  }

  const appearanceClasses =
    appearance === 'glass'
      ? [styles.glass, accent === 'green' ? styles.accentGreen : styles.accentPurple]
      : appearance === 'solid'
        ? [styles.solid, danger ? styles.solidDanger : accent === 'green' ? styles.solidGreen : accent === 'yellow' && styles.solidYellow]
        : [styles.outline];

  return (
    <AntButton
      // The solid appearance rides AntD's primary (or danger, if set) palette;
      // glass paints its own surface; outline stays a plain bordered/transparent pill.
      type={appearance === 'solid' ? 'primary' : type}
      danger={danger}
      className={[...appearanceClasses, className].filter(Boolean).join(' ')}
      {...rest}
    />
  );
};

export default Button;
